import ListGroup from "react-bootstrap/ListGroup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectExistingNetwork,
  setSelectedAsset,
} from "../../features/existingNetwork/existingNetworkSlice";
import { selectOSFeatures, setOSFeature } from "../../features/osFeatures/osFeaturesSlice";
import { MapFeature } from "../../models/enums";
import { selectSelectMapFeatures } from "../../features/selectMapFeatures/selectMapFeaturesSlice";
import { selectSteps } from "../../features/steps/stepsSlice";
import {
  DataListProperties,
  getAtCAggregateFeatureProperties,
  getAtCAssetFeatureProperties,
  getConductingSectionFeatureProperties,
  getHVFeatureProperties,
  getLVMainFeatureProperties,
  getLVServiceCableFeatureProperties,
  getOverheadPoleFeatureProperties,
  getPowerTransformerFeatureProperties,
  getThirdpartyCableFeatureProperties,
} from "../../services/propertyService";
import { layerOptionValues, layerOptions } from "../../features/layerToggle/constants";
import { ConsoleOption, selectApp } from "../../app/appSlice";
import { selectLayerToggle } from "../../features/layerToggle/layerToggleSlice";
import styles from "./DataList.module.css";
import { Button } from "react-bootstrap";
import { useEffect } from "react";

const DataList = () => {
  const { consoleMode } = useAppSelector(selectApp);
  const { osFeature } = useAppSelector(selectOSFeatures);
  const { selectedAsset } = useAppSelector(selectExistingNetwork);
  const { mode } = useAppSelector(selectSteps);
  const { selectedFeature } = useAppSelector(selectSelectMapFeatures);
  const { layers } = useAppSelector(selectLayerToggle);

  const dispatch = useAppDispatch();

  const shouldShow = () => {
    if (!mode) {
      return false;
    }

    if (
      (consoleMode === ConsoleOption.selectMapFeatures &&
        selectedFeature === MapFeature.osFeatures &&
        osFeature) ||
      (consoleMode === ConsoleOption.selectMapFeatures &&
        selectedFeature === MapFeature.ragAssets &&
        selectedAsset) ||
      (layers.includes(layerOptionValues.acceptedToConnect) &&
        (selectedAsset?.assetType === layerOptionValues.acceptedToConnect ||
          selectedAsset?.assetType === layerOptionValues.powerTransformers))
    ) {
      return true;
    } else return false;
  };

  const getSelectedFeatureProperties = () => {
    let display: DataListProperties;
    if (selectedFeature === MapFeature.osFeatures && osFeature) {
      return Object.entries(osFeature.properties);
    }
    if (selectedFeature !== MapFeature.ragAssets) return [];
    switch (selectedAsset?.assetType) {
      case layerOptionValues.conductingSections:
        display = getConductingSectionFeatureProperties(selectedAsset);
        break;
      case layerOptionValues.overheadPoles:
        display = getOverheadPoleFeatureProperties(selectedAsset);
        break;
      case layerOptionValues.powerTransformers:
        display =
          consoleMode === ConsoleOption.selectMapFeatures
            ? getPowerTransformerFeatureProperties(selectedAsset)
            : getAtCAggregateFeatureProperties(selectedAsset);
        break;
      case layerOptionValues.hvs:
        display = getHVFeatureProperties(selectedAsset);
        break;
      case layerOptionValues.lvMains:
        display = getLVMainFeatureProperties(selectedAsset);
        break;
      case layerOptionValues.lvServices:
        display = getLVServiceCableFeatureProperties(selectedAsset);
        break;
      case layerOptionValues.thirdParties:
        display = getThirdpartyCableFeatureProperties(selectedAsset);
        break;
      case layerOptionValues.acceptedToConnect:
        display = getAtCAssetFeatureProperties(selectedAsset);
        break;
      default:
        return [];
    }
    return Object.entries(display);
  };

  const clearData = () => {
    dispatch(setSelectedAsset(undefined));
    dispatch(setOSFeature(undefined));
  };

  //clear selected data on dismount
  useEffect(() => {
    return () => clearData();
  }, []);

  return (
    <>
      {shouldShow() && (
        <div className={styles.datalist}>
          <div className={styles.datalistTitle}>
            <span>
              {osFeature
                ? "OS Features"
                : Object.values(layerOptions).find(
                    (layer) => layer.value === selectedAsset?.assetType
                  )?.text}
            </span>
            <Button
              variant="danger"
              type="reset"
              className={styles.datalistButton}
              onClick={clearData}
            >
              <i className={`icon-cross-solid`} />
            </Button>
          </div>
          <div className={styles.datalistData}>
            <ListGroup>
              {getSelectedFeatureProperties().map(([key, value]) => {
                if (key === "desc") {
                  return (
                    <ListGroup.Item key={key} className="d-flex justify-content-between px-2 py-1">
                      <span>{value as string}</span>
                    </ListGroup.Item>
                  );
                } else if (key === "spid") {
                  try {
                    if (`${value}`.indexOf("-") === -1) {
                      return (
                        <ListGroup.Item
                          key={key}
                          className="d-flex justify-content-between px-2 py-1"
                        >
                          <span className="data-list__key">{key.replaceAll("_", " ")}</span>
                          <b className="text-right">{value as string}</b>
                        </ListGroup.Item>
                      );
                    } else {
                      return (
                        <ListGroup.Item
                          key={key}
                          className="d-flex justify-content-between px-2 py-1"
                        >
                          <span className="data-list__key">{key.replaceAll("_", " ")}</span>
                          <a
                            href={`${value}`.substring(`${value}`.indexOf("-") + 1)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {`${value}`.split("-")[0]}
                          </a>
                        </ListGroup.Item>
                      );
                    }
                  } catch (error) {
                    throw error;
                  }
                } else {
                  return (
                    <ListGroup.Item key={key} className="d-flex justify-content-between px-2 py-1">
                      <span className="data-list__key">{key.replaceAll("_", " ")}</span>
                      <b className="text-right">{value as string}</b>
                    </ListGroup.Item>
                  );
                }
              })}
            </ListGroup>
          </div>
        </div>
      )}
    </>
  );
};

export default DataList;
